import formatDateOrString from '@/functions/formatDateOrString.ts';
import axios from 'axios';
import moment from 'moment';
import {useCallback} from 'react';
import {z} from 'zod';
import {getStoredAdminUser, useUser} from '../components/Providers/JWTProvider';
import {useJwtSubject} from '../shared/jwt';
import type {
    Enrollment,
    EnrollmentData,
    EnrollmentExport,
    EnrollmentYear,
    Webuser,
} from '../shared/model.interface';

type DoEnrollmentStudentProps = {
    guardianId: string;
    year: string;
};

export type RaceReportRow = {
    race: string;
    studentId: string;
};

export const exportEnrollmentSchema = z
    .object({
        fromDate: z.preprocess(
            val => {
                if (!val || !moment.isMoment(val)) {
                    return undefined;
                }
                return val.toDate();
            },
            z.date({
                required_error: 'From Date is Required',
                invalid_type_error: 'Invalid date',
            }),
        ),
        toDate: z.preprocess(
            val => {
                if (!val || !moment.isMoment(val)) {
                    return undefined;
                }
                return val.toDate();
            },
            z.date({
                required_error: 'Required',
                invalid_type_error: 'Invalid date',
            }),
        ),
        type: z.string().nonempty(),
    })
    .refine(data => data.fromDate <= data.toDate, {message: '', path: ['fromDate', 'toDate']});

export type ExportEnrollment = z.infer<typeof exportEnrollmentSchema>;

export const doToggleEnrollmentYearActive = async ({id, active}: {id: string; active: boolean}) => {
    const response = await axios.post(
        `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/enrollment-year/toggle-active`,
        {id: id, active: active},
    );
    return response.data;
};

export const doEnrollmentStudent = async ({
    guardianId,
    year,
}: DoEnrollmentStudentProps): Promise<Enrollment> => {
    const response = await axios.post(
        `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/enrollment/create/${guardianId}`,
        {year: year},
    );
    return response.data;
};

type doResendEnrollmentEmailProps = {
    email: string;
    languageCode: string;
};

export const doResendEnrollmentEmail = async ({
    email,
    languageCode,
}: doResendEnrollmentEmailProps): Promise<Enrollment> => {
    const response = await axios.post(
        `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/webuser/resend/`,
        {
            email: email,
            languageCode: languageCode,
        },
    );
    return response.data;
};

export const enrollmentQueryKey = (enrollmentId: string) => ['enrollment', enrollmentId];

export const fetchEnrollment = async (id: string): Promise<Enrollment> => {
    const response = await axios.get(
        `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/enrollment/fetch/${id}`,
    );
    return response.data;
};

export const fetchEnrollmentYearData = async (): Promise<EnrollmentYear[]> => {
    const response = await axios.get(
        `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/enrollment-year/fetch/`,
    );
    return response.data;
};

export const useFetchAdminEnrollmentYearData = () => {
    const {user} = useUser();
    return useCallback(async () => {
        const response = await axios.get(
            `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/enrollment-years`,
            {
                headers: {
                    Authorization: `Bearer ${user.accessToken}`,
                    'Content-Type': 'application/json',
                },
            },
        );
        return response.data;
    }, [user]);
};

export const fetchEnrollmentDataForGuardian = async (id: string): Promise<Enrollment[]> => {
    const response = await axios.get(
        `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/enrollment/fetch-all/${id}`,
    );
    response.data.documents = [];
    return response.data;
};

export const useFetchEnrollmentDataForAdmin = () => {
    const {user} = useUser();
    return useCallback(
        async (
            resultsPerPage: number,
            page: number,
            sortField: string,
            sortOrder: string,
            enrollmentStatus: string,
            enrollmentFilter: string,
        ): Promise<EnrollmentData> => {
            const newEnrollmentStatus = enrollmentStatus === undefined ? 'all' : enrollmentStatus;
            const response = await axios.get(
                `${
                    import.meta.env.VITE_REACT_APP_API_ENDPOINT
                }/v1/admin/fetch/${resultsPerPage}/${page}/${sortField}/${sortOrder}/${newEnrollmentStatus}/${enrollmentFilter}`,
                {
                    headers: {
                        Authorization: `Bearer ${user.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                },
            );
            return response.data;
        },
        [user],
    );
};

export const useDeleteEnrollment = () => {
    const {user} = useUser();
    return useCallback(
        async (enrollmentId: string) => {
            const response = await axios.delete(
                `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/delete/${enrollmentId}`,
                {
                    headers: {
                        Authorization: `Bearer ${user.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                },
            );
            return response.data;
        },
        [user],
    );
};

export const useDeleteEnrollmentYear = () => {
    const {user} = useUser();
    return useCallback(
        async (enrollmentYearId: string) => {
            const response = await axios.delete(
                `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/enrollment-year/${enrollmentYearId}`,
                {
                    headers: {
                        Authorization: `Bearer ${user.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                },
            );
            return response.data;
        },
        [user],
    );
};

export const useFetchEnrollmentAccountsForAdmin = () => {
    const {user} = useUser();
    return useCallback(async (): Promise<Webuser[]> => {
        const response = await axios.get(
            `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/fetch/webusers`,
            {
                headers: {
                    Authorization: `Bearer ${user.accessToken}`,
                    'Content-Type': 'application/json',
                },
            },
        );
        return response.data;
    }, [user]);
};

// Past In-Progress and Awaiting Documents the API only takes admin saves, so the
// guardian form sends the stored admin token when there is one.
export const storedAdminHeaders = (): Record<string, string> => {
    const user = getStoredAdminUser();

    return user ? {Authorization: `Bearer ${user.accessToken}`} : {};
};

export const saveEnrollment = async (enrollment: Enrollment): Promise<Enrollment> => {
    const response = await axios.post(
        `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/enrollment/save/${enrollment.id}`,
        enrollment,
        {headers: storedAdminHeaders()},
    );
    return response.data;
};

export const submitEnrollment = async (enrollment: Enrollment): Promise<string> => {
    const response = await axios.post(
        `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/enrollment/submit/${enrollment.id}`,
        enrollment,
        {headers: storedAdminHeaders()},
    );
    return response.data;
};

export const saveEnrollmentNote = async (enrollment: Enrollment): Promise<Enrollment[]> => {
    const response = await axios.post(
        `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/enrollment/save-note/${enrollment.id}`,
        {note: enrollment.note},
    );
    return response.data;
};

export const useSaveEnrollmentStudentId = () => {
    const {user} = useUser();
    return useCallback(
        async (enrollment: Enrollment): Promise<Enrollment[]> => {
            const response = await axios.post(
                `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/save/studentId/${enrollment.id}`,
                {studentId: enrollment.studentId},
                {
                    headers: {
                        Authorization: `Bearer ${user.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                },
            );
            return response.data;
        },
        [user],
    );
};

export const useSaveEnrollmentStudentIsbeSisId = () => {
    const {user} = useUser();
    return useCallback(
        async (enrollment: Enrollment): Promise<Enrollment[]> => {
            const response = await axios.post(
                `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/save/studentIsbeSisId/${enrollment.id}`,
                {studentIsbeSisId: enrollment.studentIsbeSisId},
                {
                    headers: {
                        Authorization: `Bearer ${user.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                },
            );
            return response.data;
        },
        [user],
    );
};

export const useSaveEnrollmentStatus = () => {
    const {user} = useUser();
    return useCallback(
        async (enrollment: Enrollment): Promise<Enrollment[]> => {
            const response = await axios.post(
                `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/save/status/${enrollment.id}`,
                {status: enrollment.status},
                {
                    headers: {
                        Authorization: `Bearer ${user.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                },
            );
            return response.data;
        },
        [user],
    );
};

export const useImportEnrollments = () => {
    const {user} = useUser();
    const subject = useJwtSubject(user.accessToken);
    return useCallback(
        async (data: {enrollmentIds: string[]}): Promise<Enrollment[]> => {
            const {enrollmentIds} = data;

            if (!subject) {
                throw Error('User identifier not found in token.');
            }

            const response = await axios.post(
                `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/import`,
                {enrollmentIds, user: subject},
                {
                    headers: {
                        Authorization: `Bearer ${user.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                },
            );
            return response.data;
        },
        [user, subject],
    );
};

export const useDownloadImportedEnrollments = () => {
    const {user} = useUser();
    const subject = useJwtSubject(user.accessToken);
    return useCallback(
        async (data: {enrollmentIds: string[]}): Promise<Enrollment[]> => {
            const {enrollmentIds} = data;

            if (!subject) {
                throw Error('User identifier not found in token.');
            }

            const response = await axios.post(
                `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/download-imported`,
                {enrollmentIds, user: subject},
                {
                    headers: {
                        Authorization: `Bearer ${user.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                },
            );
            return response.data;
        },
        [user, subject],
    );
};

export const useDownloadRaceReportData = () => {
    const {user} = useUser();
    const subject = useJwtSubject(user.accessToken);
    return useCallback(
        async (data: {enrollmentIds: string[]}): Promise<RaceReportRow[]> => {
            const {enrollmentIds} = data;

            if (!subject) {
                throw Error('User identifier not found in token.');
            }

            const response = await axios.post(
                `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/download-race-report`,
                {enrollmentIds, user: subject},
                {
                    headers: {
                        Authorization: `Bearer ${user.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                },
            );
            return response.data;
        },
        [user, subject],
    );
};

type ExportEnrollmentOverride = Omit<ExportEnrollment, 'fromDate' | 'toDate'> & {
    fromDate: Date | '';
    toDate: Date | '' | undefined;
};

export const useExportEnrollments = () => {
    const {user} = useUser();
    return useCallback(
        async (data: ExportEnrollmentOverride): Promise<Enrollment[]> => {
            const response = await axios.post(
                `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/export`,
                {
                    fromDate: formatDateOrString(data.fromDate !== '' ? data.fromDate : new Date()),
                    toDate: formatDateOrString(
                        data.toDate === '' || !data.toDate ? new Date() : data.toDate,
                    ),
                    type: data.type,
                },
                {
                    headers: {
                        Authorization: `Bearer ${user.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                },
            );
            return response.data;
        },
        [user],
    );
};

export const useGetImportEnrollments = () => {
    const {user} = useUser();
    return useCallback(async (): Promise<Enrollment[]> => {
        const response = await axios.get(
            `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/import/get`,
            {
                headers: {
                    Authorization: `Bearer ${user.accessToken}`,
                    'Content-Type': 'application/json',
                },
            },
        );
        return response.data;
    }, [user]);
};

export const useGetExportEnrollments = () => {
    const {user} = useUser();
    return useCallback(async (): Promise<EnrollmentExport[]> => {
        const response = await axios.get(
            `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/admin/export/get`,
            {
                headers: {
                    Authorization: `Bearer ${user.accessToken}`,
                    'Content-Type': 'application/json',
                },
            },
        );
        return response.data;
    }, [user]);
};
